import {
  Clock,
  Users,
  MapPin,
  DollarSign,
  Calendar,
  Sun,
  Award,
  GraduationCap,
  MessageCircle,
  FileText,
  Target,
  CheckCircle2,
  Smartphone,
} from 'lucide-react';
import Year3FoundationsTemplate, {
  type Year3FoundationsPageData,
} from './templates/Year3FoundationsTemplate';

const PAGE: Year3FoundationsPageData = {
  route: '/programs/nsw/year-8-foundations-english-tutoring',
  breadcrumb: ['NSW programs', 'Foundations', 'Year 8 English'],
  pill: 'Year 8 · NSW curriculum',
  h1: 'Year 8 English tutoring in NSW',
  subtitle:
    'Deepen analytical reading, extended writing and language conventions in small groups - aligned to the NSW NESA Stage 4 English syllabus and a strong bridge into senior English.',
  quickFacts: [
    { icon: Clock, text: '{DURATION_SHORT}' },
    { icon: Users, text: '8-12 students' },
    { icon: MapPin, text: 'In-centre + online' },
    { icon: DollarSign, text: '{PRICE} / session' },
  ],
  courseDetailsHeading: 'Year 8 English',
  pricingHeading: 'Year 8 English',
  relatedHeading: 'More for Year 8 students',
  yearLevel: 8,
  tabs: [
    {
      id: 'term',
      icon: Calendar,
      name: 'Term course',
      sub: '10 weekly sessions per term',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Term Course overview',
          para:
            'The Year 8 English term course runs 10 sessions per term, {DURATION_LENGTH} each, in small groups of 8-12 students. Lessons consolidate NESA Stage 4 outcomes and build the analytical writing habits required in Stage 5.',
          bullets: [
            'Analytical reading, extended writing and grammar covered across the term',
            'Content sequenced alongside the school year so tutoring and classroom work reinforce each other',
            'Strong preparation for in-school assessments, scholarship exams and Stage 5 English',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'This course is ideal for students who…',
          bullets: [
            'Want to consolidate Stage 4 English before stepping into Stage 5',
            'Are sitting private school scholarship exams',
            'Aim for stronger performance in class tasks and assessments',
            'Are building writing confidence through regular guided practice',
            '(Parents) want progress to be visible and reported each term',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para:
            'By the end of the term your child will have made measurable progress in:',
          bullets: [
            'Analytical reading - text analysis, language features, theme and context',
            'Extended writing - narrative, persuasive and analytical responses',
            'Grammar and editing - complex sentence structure, paragraphing and revision',
            'Assessment readiness - extended responses and essays under timed conditions',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'Topics are sequenced across the four school terms:',
          bullets: [
            'Term 1 - Analytical reading: novels, poetry and language analysis',
            'Term 2 - Narrative and descriptive writing: voice, structure and craft',
            'Term 3 - Persuasive writing and scholarship-style responses',
            'Term 4 - Essay writing: text response, structure and editing for Stage 5',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'Every session follows a consistent structure so students always know what to expect:',
          bullets: [
            'Free level assessment before the first session - no wasted lessons on work already mastered',
            'Each 60-min lesson: introduce the concept → model examples → guided practice → independent application',
            "Written and verbal feedback on every student's work each session",
            "End-of-term progress report so parents can see clearly what's improved",
          ],
        },
      ],
    },
    {
      id: 'holiday',
      icon: Sun,
      name: 'Holiday intensive',
      sub: '4 daily lessons in the holidays',
      items: [
        {
          label: 'Overview',
          name: 'Course overview',
          heading: 'Holiday intensive overview',
          para:
            'The Year 8 English holiday intensive runs 4 daily sessions over the school holidays, 60 minutes each. The program focuses on one writing or analysis genre intensively, building skill and confidence quickly.',
          bullets: [
            '4 sessions, daily, 60 minutes each',
            'One genre - deep focus rather than broad coverage',
            'Same small groups and qualified educators as the term course',
            'No prior Schoolola enrolment needed',
          ],
        },
        {
          label: 'Audience',
          name: "Who it's for",
          heading: "Who it's for",
          para: 'The holiday intensive suits students who…',
          bullets: [
            'Are preparing for scholarship exams or upcoming assessments',
            'Want a focused short boost before the next school term begins',
            "Can't commit to a full term but want structured, quality tuition",
            'Need to build confidence in one specific writing or analysis style quickly',
          ],
        },
        {
          label: 'Benefits',
          name: "What you'll gain",
          heading: "What you'll gain",
          para: 'After 4 sessions your child will have:',
          bullets: [
            'A solid understanding of one genre - structure, language features, planning approach',
            'Writing strategies for assessments and scholarship exams under timed conditions',
            'Increased confidence to tackle writing and analysis tasks at school',
            'Specific written feedback on their own practice writing',
          ],
        },
        {
          label: 'Content',
          name: "What's covered",
          heading: "What's covered",
          para: 'The four sessions follow a structured daily sequence:',
          bullets: [
            'Day 1 - Genre overview: model texts, key features, planning framework',
            'Day 2 - Planning and drafting: scaffold practice, vocabulary development',
            'Day 3 - Revision and editing: sentence variety, language choices, checking against criteria',
            'Day 4 - Practice task under assessment conditions with full written feedback',
          ],
        },
        {
          label: 'Program',
          name: 'How it works',
          heading: 'How it works',
          para:
            'The intensive is designed for maximum impact across four consecutive sessions:',
          bullets: [
            'Register your interest - sessions fill quickly in school holiday periods',
            'Each day builds on the previous - daily attendance is expected',
            'Individual written feedback on Day 3 and Day 4 tasks included',
            'Students leave with a completed writing portfolio piece',
          ],
        },
      ],
    },
  ],
  inclusions: [
    {
      icon: Award,
      name: 'Qualified educators',
      desc: 'Teachers with NSW curriculum expertise and classroom experience - consistent across all NSW centres.',
    },
    {
      icon: Users,
      name: 'Small group classes',
      desc: '8-12 students per class. Your child gets seen, heard and responded to every lesson.',
    },
    {
      icon: GraduationCap,
      name: 'NSW curriculum-aligned',
      desc: 'Content mapped directly to NESA Stage 4 outcomes. Complements school, session by session.',
    },
    {
      icon: MessageCircle,
      name: 'Feedback every lesson',
      desc: "Structured written and verbal feedback on every student's work, every session.",
    },
    {
      icon: FileText,
      name: 'End-of-term report',
      desc: 'A clear parent-facing progress summary at end of each term - before school reports arrive.',
    },
    {
      icon: Target,
      name: 'Free assessment',
      desc: 'Level placement before the first session. No wasted lessons on work already mastered.',
    },
    {
      icon: CheckCircle2,
      name: 'Scholarship & Stage 5 ready',
      desc: 'Year 8 content supports scholarship exam preparation and builds the analytical habits required across Stage 5 English.',
    },
    {
      icon: Smartphone,
      name: 'Schoolola LMS',
      desc: 'Access theory lesson videos, online practice questions and track progress easily via the Schoolola LMS app.',
      images: [
        '/lovable-uploads/lms-parent-dashboard.png',
        '/lovable-uploads/lms-learning-dashboard.png',
        '/lovable-uploads/lms-diagnostic-overview.png',
        '/lovable-uploads/lms-quiz-question-1.png',
        '/lovable-uploads/lms-quiz-question-2.png',
        '/lovable-uploads/lms-results-report.png',
      ],
    },
  ],
  faq: [
    {
      q: 'Is {PRICE} the full price - are there any extras?',
      a: '{PRICE} covers the session. Term enrolments include the end-of-term progress report at no extra cost. {BOOKS}',
    },
    {
      q: 'What study materials are provided?',
      a: 'Study materials are provided as bound books - no loose papers. {BOOKS}',
    },
    {
      q: 'How is this different from what happens at school?',
      a: "Group sizes are smaller (8-12 students per class), lessons follow a consistent structured format, and every student receives written feedback on their own work each session. Content follows the same NESA Stage 4 outcomes - we're giving your child more practice and more attention within the same curriculum.",
    },
    {
      q: 'What if my child misses a session?',
      a: 'We ask for consistent attendance as each session builds on the last. If a session is missed, your educator can note the key material to follow up at home - but there are no standard make-up classes. Contact your local centre.',
    },
    {
      q: 'Can a confident Year 8 student still be challenged?',
      a: 'Yes. The assessment places students within ability groups, not just year groups. A Year 8 student working above level will be placed in a group that challenges them accordingly.',
    },
  ],
  related: [
    {
      tag: 'Year 8 · NSW',
      name: 'Year 8 Maths',
      desc: 'Algebra, geometry and reasoning',
      href: '/programs/nsw/year-8-foundations-maths-tutoring',
    },
    {
      tag: 'Year 7-8 · NSW',
      name: 'Scholarship Exam Course',
      desc: 'Private school scholarship preparation',
      href: '/programs/scholarship-exam-course',
    },
  ],
  seo: {
    title: 'Year 8 English Tutoring NSW - Small Groups | Schoolola',
    description:
      'Year 8 English tutoring in NSW. NESA Stage 4 aligned analytical reading, extended writing and grammar with scholarship and Stage 5 readiness. Small groups of 8-12 students..',
    schemaCourseName: 'Year 8 English Tutoring - NSW',
    schemaDescription:
      'Year 8 English tuition in NSW. Small groups of 8-12 students, aligned to NESA Stage 4. Analytical reading, extended writing, grammar and scholarship/Stage 5 readiness..',
  },
};

export default function NswYear8FoundationsEnglish() {
  return <Year3FoundationsTemplate page={PAGE} />;
}
